import { useCallback, useEffect, useState } from "react";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";
import { useThor } from "../context/ThorContext";
import SmallLoadingSpinner from "../../common/elements/SmallLoadingSpinner";
import InfoCard from "../../common/elements/InfoCard";
import Button from "../../common/elements/Button";
import { serverErrMsg } from "../utils/validators";

const PAGE_SIZE = 20;

const FIELD_LABELS = {
    hypothesisIf: 'Hypothesis (If…)',
    hypothesisThen: 'Hypothesis (Then…)',
    hypothesisBecause: 'Hypothesis (Because…)',
    longTermStrategicGoal: 'Long-term Strategic Goal',
    backgroundResearch: 'Background Research',
    audienceDescription: 'Audience Description',
    targetedStartDate: 'Target Start Date',
    daysToPower: 'Days to Power',
    productManager: 'Product Manager',
    jiraTicket: 'Jira Ticket',
    specUrl: 'Spec URL',
    isLongRunning: 'Long-running',
    memberTest: 'Member Test',
    analysisRequired: 'Analysis Required',
    adHocAnalysis: 'Ad-hoc Analysis',
    trafficAllocation: 'Traffic Allocation (%)',
    experimentType: 'Experiment Type',
};
const humanizeField = (key) => FIELD_LABELS[key] ?? key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());

const TABLE_LABELS = {
    experiment: 'Experiment',
    metric: 'Metric',
    bucket: 'Bucket',
    audience: 'Audience',
    segment: 'Segment',
    variable: 'Variable',
    experiment_classification: 'Classification',
    instrumentation_instructions: 'Instrumentation',
    experiment_reviews: 'Approval',
    experiment_stakeholders: 'Stakeholders',
    qualifier: 'Qualifier',
    override: 'Override',
    experiment_segment: 'Segment',
    experiment_schedule: 'Schedule',
    experiment_analysis: 'Analysis',
    experiment_result: 'Outcome',
};
const OP_LABELS = { insert: 'Added', update: 'Updated', delete: 'Deleted' };
const humanTable = (t) => TABLE_LABELS[t?.toLowerCase()] ?? t;
const humanOp = (op) => OP_LABELS[op?.toLowerCase()] ?? op;

const fmtDate = (dt) => dt
    ? new Date(dt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

const OP_COLORS = { INSERT: 'is-success is-light', UPDATE: 'is-info is-light', DELETE: 'is-danger is-light' };

// ── JSON diff viewer ───────────────────────────────────────────────────────

const SKIP_KEYS = new Set(['updatedAt', 'createdAt', 'auditVersion', 'experimentId', 'id']);

const fmtVal = (v) => {
    if (v === null || v === undefined) return <span className="has-text-grey is-size-7">null</span>;
    if (typeof v === 'boolean') return <code className="is-size-7">{String(v)}</code>;
    if (typeof v === 'object') return <code style={{ fontSize: '0.7rem', wordBreak: 'break-all' }}>{JSON.stringify(v)}</code>;
    return <span style={{ wordBreak: 'break-all' }}>{String(v)}</span>;
};

const DiffView = ({ prev, next, op }) => {
    if (op === 'INSERT' && next) {
        const keys = Object.keys(next).filter(k => !SKIP_KEYS.has(k));
        return (
            <table className="table is-fullwidth is-size-7 mb-0">
                <thead><tr>
                    <th style={{ width: '35%' }}>Field</th>
                    <th style={{ color: '#257942' }}>Added value</th>
                </tr></thead>
                <tbody>
                    {keys.map(k => (
                        <tr key={k} style={{ background: '#f0fff4' }}>
                            <td>{humanizeField(k)}</td>
                            <td style={{ color: '#257942' }}>{fmtVal(next[k])}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }
    if (op === 'DELETE' && prev) {
        const keys = Object.keys(prev).filter(k => !SKIP_KEYS.has(k));
        return (
            <table className="table is-fullwidth is-size-7 mb-0">
                <thead><tr>
                    <th style={{ width: '35%' }}>Field</th>
                    <th style={{ color: '#cc0f35' }}>Removed value</th>
                </tr></thead>
                <tbody>
                    {keys.map(k => (
                        <tr key={k} style={{ background: '#fff5f7' }}>
                            <td>{humanizeField(k)}</td>
                            <td style={{ color: '#cc0f35', textDecoration: 'line-through' }}>{fmtVal(prev[k])}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }
    // UPDATE — show changed fields only
    const prevObj = prev ?? {};
    const nextObj = next ?? {};
    const allKeys = [...new Set([...Object.keys(prevObj), ...Object.keys(nextObj)])].filter(k => !SKIP_KEYS.has(k));
    const changed = allKeys.filter(k => JSON.stringify(prevObj[k]) !== JSON.stringify(nextObj[k]));
    if (changed.length === 0) {
        return <p className="has-text-grey is-size-7 p-5 has-text-centered">No meaningful field changes detected.</p>;
    }
    return (
        <table className="table is-fullwidth is-size-7 mb-0">
            <thead><tr>
                <th style={{ width: '30%' }}>Field</th>
                <th style={{ width: '35%' }}>Before</th>
                <th style={{ width: '35%' }}>After</th>
            </tr></thead>
            <tbody>
                {changed.map(k => (
                    <tr key={k}>
                        <td style={{ fontWeight: 600 }}>{humanizeField(k)}</td>
                        <td style={{ background: '#fff0f0', color: '#9a1c1c' }}>{fmtVal(prevObj[k])}</td>
                        <td style={{ background: '#f0fff4', color: '#1a6e3c' }}>{fmtVal(nextObj[k])}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const DiffModal = ({ isActive, entry, onClose }) => {
    const [showRaw, setShowRaw] = useState(false);
    if (!entry) return null;

    const fmt = (json) => {
        if (!json) return null;
        try { return JSON.stringify(JSON.parse(json), null, 2); }
        catch { return json; }
    };

    let prev = null, next = null;
    try { prev = entry.previousState ? JSON.parse(entry.previousState) : null; } catch {}
    try { next = entry.newState ? JSON.parse(entry.newState) : null; } catch {}

    const rawPrev = fmt(entry.previousState);
    const rawNext = fmt(entry.newState);
    const op = entry.operation?.toUpperCase();

    return (
        <div className={`modal ${isActive ? 'is-active' : ''}`}>
            <div className="modal-background" onClick={onClose} />
            <div className="modal-card" style={{ width: '860px', maxWidth: '95vw' }}>
                <header className="modal-card-head">
                    <p className="modal-card-title">
                        {humanOp(entry.operation)} — {humanTable(entry.tableName)} — {fmtDate(entry.auditedAt)}
                    </p>
                    <button className="delete" onClick={onClose} />
                </header>
                <section className="modal-card-body" style={{ padding: 0, overflowY: 'auto', maxHeight: '65vh' }}>
                    {showRaw ? (
                        <div style={{ display: 'flex', minHeight: '200px' }}>
                            <div style={{ flex: 1, padding: '1rem', borderRight: '1px solid #dbdbdb', overflowY: 'auto' }}>
                                <p className="has-text-weight-semibold is-size-7 has-text-grey mb-2 is-uppercase" style={{ letterSpacing: '0.05em' }}>Previous State</p>
                                {rawPrev
                                    ? <pre style={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{rawPrev}</pre>
                                    : <p className="has-text-grey is-size-7">—</p>}
                            </div>
                            <div style={{ flex: 1, padding: '1rem', overflowY: 'auto' }}>
                                <p className="has-text-weight-semibold is-size-7 has-text-grey mb-2 is-uppercase" style={{ letterSpacing: '0.05em' }}>New State</p>
                                {rawNext
                                    ? <pre style={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{rawNext}</pre>
                                    : <p className="has-text-grey is-size-7">—</p>}
                            </div>
                        </div>
                    ) : (
                        <DiffView prev={prev} next={next} op={op} />
                    )}
                </section>
                <footer className="modal-card-foot" style={{ justifyContent: 'space-between' }}>
                    <button type="button" className="button is-ghost is-small has-text-grey"
                            onClick={() => setShowRaw(r => !r)}>
                        {showRaw ? '← Show diff' : 'Show raw JSON'}
                    </button>
                    <Button onClick={onClose}>Close</Button>
                </footer>
            </div>
        </div>
    );
};

// ── Main component ─────────────────────────────────────────────────────────

const ChangeLogTab = ({ experimentKey }) => {
    const { execute } = useFetchWithMsal();
    const { addToast } = useThor();

    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [totalRows, setTotalRows] = useState(0);
    const [diffEntry, setDiffEntry] = useState(null);

    const fetchPage = useCallback((p) => {
        setLoading(true);
        execute('GET', `${thor_api_urls.experimentAudit(experimentKey)}?page=${p}&size=${PAGE_SIZE}&sort=desc`)
            .then(data => {
                setEntries(data?.experimentAuditEntries ?? []);
                setLastPage(data?.lastPage ?? 1);
                setTotalRows(data?.totalRows ?? 0);
                setPage(p);
            })
            .catch(err => {
                console.error("Failed to fetch change log:", err);
                addToast(serverErrMsg(err, 'Failed to load change log.'), 'danger');
            })
            .finally(() => setLoading(false));
    }, [execute, experimentKey, addToast]);

    useEffect(() => { fetchPage(1); }, [fetchPage]);

    return (
        <>
            <div className="level mb-3">
                <div className="level-left">
                    <p className="subtitle is-5 mb-0">Change Log</p>
                </div>
                {totalRows > 0 && (
                    <div className="level-right">
                        <span className="has-text-grey is-size-7">{totalRows} entries</span>
                    </div>
                )}
            </div>

            {loading ? <SmallLoadingSpinner /> : entries.length === 0 ? (
                <InfoCard title="No change log entries for this experiment." />
            ) : (
                <>
                    <table className="table is-fullwidth is-striped is-hoverable">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>User</th>
                                <th>What changed</th>
                                <th>Action</th>
                                <th className="has-text-centered">Diff</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map(e => (
                                <tr key={e.experimentAuditId}>
                                    <td className="has-text-grey is-size-7" style={{ whiteSpace: 'nowrap' }}>{fmtDate(e.auditedAt)}</td>
                                    <td className="is-family-monospace" style={{ fontSize: '0.8rem' }}>{e.username ?? '—'}</td>
                                    <td>
                                        <span className="tag is-dark is-light is-small">{humanTable(e.tableName)}</span>
                                    </td>
                                    <td>
                                        <span className={`tag is-small ${OP_COLORS[e.operation?.toUpperCase()] ?? 'is-light'}`}>{humanOp(e.operation)}</span>
                                    </td>
                                    <td className="has-text-centered">
                                        <Button className="is-small is-light"
                                                disabled={!e.previousState && !e.newState}
                                                onClick={() => setDiffEntry(e)}>
                                            View
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Pagination */}
                    {lastPage > 1 && (
                        <nav className="pagination is-small is-centered mt-4" role="navigation">
                            <Button className="pagination-previous is-small"
                                    disabled={page <= 1} onClick={() => fetchPage(page - 1)}>
                                Previous
                            </Button>
                            <Button className="pagination-next is-small"
                                    disabled={page >= lastPage} onClick={() => fetchPage(page + 1)}>
                                Next
                            </Button>
                            <ul className="pagination-list">
                                <li>
                                    <span className="pagination-link is-current" style={{ pointerEvents: 'none' }}>
                                        Page {page} of {lastPage}
                                    </span>
                                </li>
                            </ul>
                        </nav>
                    )}
                </>
            )}

            <DiffModal isActive={!!diffEntry} entry={diffEntry} onClose={() => setDiffEntry(null)} />
        </>
    );
};

export default ChangeLogTab;
